import { CursorPaged, Id, Nillable } from '../../../../types/definitions'
import BillingMethod from 'billing/models/BillingMethod'
import JobStore from 'jobs/JobStore'
import Job, { BalanceStatus, Resolution } from 'jobs/Job'
import { computed, observable, action, reaction, makeObservable } from 'mobx'
import { formatMoney } from 'utils/money-util'
import { CloseJobDialogPorts } from '../CloseJobDialogAdapter'
import ModalViewModel from 'infra/ModalViewModel'
import { task } from 'mobx-task'
import { filter } from 'rxjs/operators'
import { GetCurrentMember } from 'iam/SessionStore'

/**
 * A resolution type.
 */
export type ViewResolution = 'Done' | 'Cancelled' | 'Test'

/**
 * A presentation-representation of a Job billing method.
 */
export interface ViewJobBillingMethod {
  /**
   * ID for this billing method.
   */
  id: Id | 'default'

  /**
   * Display friendly name for this billing method.
   */
  displayName: string
}

/**
 * Static `JobBillingMethod` operations.
 *
 * NOTICE: This is a view representation of a Job billing method.
 */
export const JobBillingMethod = {
  /**
   * Creates a default `ViewJobBillingMethod`.
   */
  get default(): ViewJobBillingMethod {
    return {
      id: 'default',
      displayName: 'Default',
    }
  },
}

/**
 * View Model for closing a job as an admin.
 */
export default class CloseJobAsAdminDialogViewModel extends ModalViewModel {
  @observable
  resolution: Resolution = Resolution.Done

  @observable
  reason = ''

  @observable
  shouldBillJob = true

  @observable
  selectedBillingMethod: ViewJobBillingMethod = JobBillingMethod.default

  constructor(
    private readonly job: Job,
    private readonly jobStore: JobStore,
    private readonly adapter: CloseJobDialogPorts,
    private getCurrentMember: GetCurrentMember,
  ) {
    super()

    makeObservable(this)

    this.job.statusChange$
      .pipe(
        filter((s) => s === 'CLOSED'),
        this.whileShowing(),
      )
      .subscribe(this.close)

    reaction(
      () => this.resolvable,
      (isResolvable) => {
        this.resolution = isResolvable ? Resolution.Done : Resolution.Cancelled
      },
      { fireImmediately: true },
    )
  }

  /**
   * Ways in which the job's resolution can be specified based on its status.
   */
  @computed
  get resolutionItems() {
    return [
      ...(this.resolvable ? [Resolution.Done] : []),
      Resolution.Cancelled,
      Resolution.Test,
    ]
  }

  /**
   * Indicates whether the Administrator should be able to bill this job.
   */
  @computed
  get billingAllowed() {
    return (
      this.job.isBillingEnabled &&
      this.job.balanceStatus !== BalanceStatus.BalanceZero
    )
  }

  /**
   * Indicates whether we should collect the billing method to charge from
   * the Admin.
   */
  @computed
  get shouldCollectBillingMethod() {
    return this.billingAllowed && this.shouldBillJob
  }

  /**
   * Indicates whether the Admin has the ability to charge or refund the
   * payer based on the payment status/outstanding balance.
   */
  @computed
  get billingOperationMessage() {
    const owner = this.job.ownerTeam ? 'team' : 'client'
    const operationType =
      this.job.balanceStatus === BalanceStatus.BalanceNegative
        ? 'Refund'
        : 'Bill'
    const prefix = `${operationType} the ${owner} the final amount`

    return `${prefix} (${formatMoney(Math.abs(this.job.balance))})`
  }

  /**
   * The name of the job.
   */
  @computed
  get jobName() {
    return this.job.name
  }

  /**
   * Closes the job with the reason and resolution specified by the Admin.
   */
  closeJob = task.resolved(async () => {
    switch (this.resolution) {
      case Resolution.Done:
        await this.jobStore.complete(
          Id.assert(this.job.id),
          this.reason,
          this.shouldBillJob,
          Id.assert(this.selectedBillingMethod.id),
        )
        break
      case Resolution.Cancelled:
        await this.jobStore.cancel(
          Id.assert(this.job.id),
          this.reason,
          this.shouldBillJob,
          Id.assert(this.selectedBillingMethod.id),
        )
        break
      case Resolution.Test:
        await this.jobStore.closeJobAsTest(
          Id.assert(this.job.id),
          this.reason,
          this.shouldBillJob,
          Id.assert(this.selectedBillingMethod.id),
        )
        break
    }

    this.close()
  })

  /**
   * Fetches billing methods.
   */
  fetchBillingMethods = async () => {
    if (!this.getCurrentMember().hasPermission('ADMIN_BILLING_MANAGE')) {
      return CursorPaged.fromArray([])
    }

    await this.adapter.fetchBillingProfiles()

    const billingMethods = this.adapter
      .getBillingMethods()
      .map(formatBillingMethod)
      .concat(JobBillingMethod.default)

    return CursorPaged.fromArray(billingMethods)

    /**
     * Formats a billing method into its ID, owner, and display name.
     */
    function formatBillingMethod(method: BillingMethod): ViewJobBillingMethod {
      return {
        id: method.id,
        displayName:
          method.type === 'Credit'
            ? 'Team Credit'
            : `${method.brand} ending in ${method.last4}`,
      }
    }
  }

  /**
   * Sets the billing method. Defaults to `JobBillingMethod.default` if not provided.
   */
  @action.bound
  setBillingMethod(billingMethod: Nillable<ViewJobBillingMethod>) {
    this.selectedBillingMethod = billingMethod || JobBillingMethod.default
  }

  /**
   * Indicates whether the job is in a state where it might be possible that
   * it's done/complete.
   */
  @computed
  private get resolvable() {
    return (
      this.job.status === 'CLAIMED' ||
      this.job.status === 'ON_HOLD' ||
      this.job.status === 'IDLE' ||
      this.job.status === 'INFO_GATHERING'
    )
  }
}
